import { FC, SyntheticEvent } from 'react'
import { Pagination } from '@cargomart/ui'
import IconAdd from 'assets/add.svg?react'
import IconRefresh from 'assets/refresh.svg?react'
import IconYarche from 'assets/yarche.svg?react'
import cn from 'classnames'
import { Widget } from 'components/extend'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { MODAL_IDS } from 'components/ModalProvider'
import { formatDateToMoscow } from 'helpers/dates'
import { useIsLaptop, useIsLargeLaptop, useIsMobile, useIsTablet } from 'hooks/media'
import { formattedOrderPaymentStatus, formattedOrderStatus, getPrice, showPaymentStatus } from 'modules/order/helpers'
import { Button } from 'shared/components/Button'
import { orderYarcheCreate } from 'utils/feature'

import { OrderFilter } from '../OrderFilter'
import { OrderTableEmpty } from './components/OrderTableEmpty'
import { OrderTableColumn, OrderTableColumnHead, OrderTableColumnSkeleton } from './components'
import { OrderTableProps } from './types'

import s from './OrderTable.module.less'
import cs from 'src/common.module.less'

export const OrderTable: FC<OrderTableProps> = (props) => {
  const {
    orders,
    pagination,
    setPagination,
    isLoading,
    refetch,
    departureDateFrom,
    departureDateTo,
    handleSetDepartureDateFrom,
    handleSetDepartureDateTo,
    status,
    handleSetStatus,
    paymentStatus,
    handleSetPaymentStatus,
    departure,
    destination,
    handleSetDeparture,
    handleSetDestination,
    handleResetFilter,
    handleSwapPoints,
    handleOpenModalDetail,
    handleOpenModalCancel,
    handleOpenOrderCreate,
    onOpenOrderCreateYarche,
    search,
    mode,
    handleSetSearch,
    handleSetMode
  } = props
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isLaptop = useIsLaptop()
  const isLargeLaptop = useIsLargeLaptop()

  const skeleton = [...new Array(10)].map((_, i) => (
    <OrderTableColumnSkeleton key={i} isMobile={isMobile} isTablet={isTablet} />
  ))

  const formattedItems = () =>
    orders?.map((order) => ({
      id: order.id,
      col1: { serialId: order.serialId, status: order.status },
      col2: { departureTime: order.departureDate },
      col3: {
        departurePointName: order.departure?.pointName,
        departurePartnerName: order.departure?.partnerName,
        departurePointType: order.departure?.pointType,
        destinationPointName: order.destination?.pointName,
        destinationPartnerName: order.destination?.partnerName,
        destinationPointType: order.destination?.pointType
      },
      col4: {
        barcode: order.barcode,
        totalWeight: order.cargoInfo?.weight,
        totalVolume: parseFloat(order.cargoInfo?.volume.toFixed(4) || '')
      },
      col5: { price: order.totalPrice, paymentStatus: formattedOrderPaymentStatus(order.paymentStatus) },
      col6: { action: order.access }
    }))

  const allFilterLength =
    (status?.length ? 1 : 0) +
    (paymentStatus?.length ? 1 : 0) +
    (departureDateFrom?.length || departureDateTo?.length ? 1 : 0) +
    (departure?.length || destination?.length ? 1 : 0)

  return (
    <>
      <Widget>
        <div className={cs.positionRelative} data-test-id="order--table">
          <div className={s.headBlock}>
            <div className={s.headBlockDesktop}>
              <h2 className={s.title}>Заявки</h2>
              <div className={s.buttons}>
                <IconRefresh
                  className={cn(s.refreshBtn, { [s.refreshBtnLoading]: isLoading })}
                  onClick={isLoading ? undefined : refetch}
                  size={isTablet ? 'nm' : 'sm'}
                />
                {!isTablet && (
                  <>
                    {orderYarcheCreate && (
                      <Button
                        className={s.createBtn}
                        color="primary"
                        onClick={onOpenOrderCreateYarche}
                        variant="outline"
                      >
                        <IconYarche />
                        Отправить посылку
                      </Button>
                    )}
                    <Button className={s.createBtn} color="primary" onClick={handleOpenOrderCreate} variant="solid">
                      Отправить груз
                    </Button>
                  </>
                )}
                {isTablet && (
                  <>
                    {orderYarcheCreate && (
                      <Button
                        className={s.createBtn}
                        color="primary"
                        onClick={onOpenOrderCreateYarche}
                        size="lg"
                        variant="outline"
                      >
                        <IconYarche />
                      </Button>
                    )}
                    <Button
                      className="!min-h-[38px] !min-w-[38px] !p-0"
                      color="primary"
                      onClick={handleOpenOrderCreate}
                      size="lg"
                      variant="solid"
                    >
                      <IconAdd />
                    </Button>
                  </>
                )}
              </div>
            </div>
            <OrderFilter
              allFilterLength={allFilterLength}
              departure={departure}
              departureDateFrom={departureDateFrom}
              departureDateTo={departureDateTo}
              destination={destination}
              handleResetFilter={handleResetFilter}
              handleSetDeparture={handleSetDeparture}
              handleSetDepartureDateFrom={handleSetDepartureDateFrom}
              handleSetDepartureDateTo={handleSetDepartureDateTo}
              handleSetDestination={handleSetDestination}
              handleSetMode={handleSetMode}
              handleSetPaymentStatus={handleSetPaymentStatus}
              handleSetSearch={handleSetSearch}
              handleSetStatus={handleSetStatus}
              handleSwapPoints={handleSwapPoints}
              id={MODAL_IDS.OrderFilterTable}
              isLaptop={isLaptop}
              isLargeLaptop={isLargeLaptop}
              isLoading={isLoading}
              isTablet={isTablet}
              mode={mode}
              orderLength={formattedItems()?.length}
              paymentStatus={paymentStatus}
              search={search}
              status={status}
            />
          </div>
          <div className={s.table} data-name="table">
            {!isTablet && <OrderTableColumnHead />}

            {!isLoading
              ? Boolean(formattedItems()?.length) &&
                formattedItems()?.map((el: any) => (
                  <OrderTableColumn
                    key={el.id}
                    action={el.col6.action}
                    barcode={el.col4.barcode}
                    departureName={el.col3.departurePointName}
                    departurePartner={el.col3.departurePartnerName}
                    departurePointType={el.col3.departurePointType}
                    departureTime={formatDateToMoscow(el.col2.departureTime)}
                    destinationName={el.col3.destinationPointName}
                    destinationPartner={el.col3.destinationPartnerName}
                    destinationPointType={el.col3.destinationPointType}
                    id={el.id}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    onCancelButtonClick={(e: SyntheticEvent) => handleOpenModalCancel(e, el)}
                    onClick={() => handleOpenModalDetail(el)}
                    paymentStatus={showPaymentStatus(el.col5.paymentStatus)}
                    price={getPrice(el.col5.price)}
                    serialId={el.col1.serialId}
                    status={formattedOrderStatus(el.col1.status)}
                    totalVolume={el.col4.totalVolume}
                    totalWeight={el.col4.totalWeight}
                  />
                ))
              : skeleton}
          </div>

          {!isLoading ? (
            Boolean(formattedItems()?.length) && (
              <div className={s.pagination}>
                <Pagination
                  currentPage={pagination.page}
                  currentPerPage={pagination.perPage}
                  perPageOptions={orders?.length !== 0 ? [5, 10, 20] : []}
                  total={orders?.length !== 0 ? pagination?.total : 0}
                  onChangePage={(page) => {
                    setPagination((v) => ({ ...v, page }))
                  }}
                  onChangePerPage={(perPage) => {
                    setPagination((v) => ({ ...v, perPage, page: 1 }))
                  }}
                />
              </div>
            )
          ) : (
            <div className={s.pagination}>
              <PlaceholderCell height={28} />
            </div>
          )}

          <OrderTableEmpty
            isEmpty={!allFilterLength && !formattedItems()?.length}
            isLoading={isLoading}
            isVisible={!formattedItems()?.length}
            mode={mode}
          />
        </div>
      </Widget>
    </>
  )
}
